import type { ContentInterface } from '../amplienceTypes/content';
import type { GenericLayoutSlot } from '../amplienceTypes/schemas/imported/generic-layout-slot-schema';
import type { MasterLayout as MasterLayoutSchema } from '../amplienceTypes/schemas/imported/master-layout-schema';
import type { MasterLayoutSlot } from '../amplienceTypes/schemas/imported/master-layout-slot-schema';
import type { PropsWithChildren, ReactElement, ReactNode } from 'react';

import dynamic from 'next/dynamic';
import { memo } from 'react';
import isEqual from 'react-fast-compare';
import styled, { css } from 'styled-components';

import { AlgoliaCore } from '~/components/productList/AlgoliaCore';
import { AlgoliaErrorBoundary } from '~/components/productList/AlgoliaErrorBoundary';
import { ProductListSection } from '~/components/productList/section/ProductListSection';
import { MasterLayout } from '~/components/shared/core/layouts/MasterLayout';
import { GenericErrorBoundary } from '~/components/shared/error/GenericErrorBoundary';
import { AlgoliaPreconnect } from '~/components/shared/metaHead/ProductList/AlgoliaPreconnect';
import { ProductListDynamicMetaHead } from '~/components/shared/metaHead/ProductList/DynamicMetaHead';
import { ProductListStaticMetaHead } from '~/components/shared/metaHead/ProductList/StaticMetaHead';
import type { PLPSpecificProps } from '~/types/props';
import { PageTypes } from '~/utilities/constants';
import { ProductListContextProvider } from '~/utilities/context/static/ProductListContext';

const ClearPreviewModeCookiesButton = dynamic(
  async () =>
    (await import('~/components/shared/cookie/ClearPreviewModeCookiesButton'))
      .ClearPreviewModeCookiesButton
);

interface ProductListViewProps extends PLPSpecificProps {
  genericLayoutSlotContent: (ContentInterface & Pick<GenericLayoutSlot, 'pageProperties'>) | null;
  masterLayoutSlotContent: (MasterLayoutSlot & { slotContent: MasterLayoutSchema }) | null;
  isPreviewMode: boolean;
  pageType: PageTypes;
  moodBanner?: ReactNode;
}

const GenericError = dynamic(async () => import('~/components/shared/error/GenericError'), {
  ssr: false,
});

const S = {
  Wrapper: styled.div<{ $setMinHeight?: boolean }>`
    ${({ $setMinHeight }) =>
      $setMinHeight &&
      css`
        min-height: 100dvh;
      `}
  `,
};

const ProductListViewComponent = ({
  // From Amplience
  plpSlotContent,
  genericLayoutSlotContent,
  masterLayoutSlotContent,
  // System texts coming from amplience for PLP
  plpSystemTextsContent,

  // From Hybris
  categoryDetails,

  // SSR
  isPreviewMode,

  // Page Type
  pageType,

  children,
  moodBanner,
}: PropsWithChildren<ProductListViewProps>): ReactElement => {
  const isPLP = pageType === PageTypes.CATEGORY;
  const metadataContent = genericLayoutSlotContent?.metaData;
  const uspListContent = genericLayoutSlotContent?.uspList;
  const pagePropertiesContent = genericLayoutSlotContent?.pageProperties;

  return (
    <>
      {isPreviewMode && <ClearPreviewModeCookiesButton />}

      <MasterLayout
        pageProperties={pagePropertiesContent}
        pageType={pageType}
        isPLP={isPLP}
        uspListContent={uspListContent}
        {...(masterLayoutSlotContent?.slotContent as MasterLayoutSchema)}
      >
        <ProductListContextProvider
          pageContent={plpSlotContent?.page}
          systemTexts={plpSystemTextsContent}
          categoryDetails={categoryDetails}
          isPLP={isPLP}
        >
          <ProductListStaticMetaHead pageType={pageType} metaData={metadataContent} />
          <AlgoliaPreconnect />

          {moodBanner}

          <S.Wrapper $setMinHeight={!!plpSlotContent?.page?.seo}>
            <AlgoliaCore>
              <ProductListDynamicMetaHead />

              <AlgoliaErrorBoundary>
                <GenericErrorBoundary fallback={<GenericError />}>
                  <ProductListSection />
                </GenericErrorBoundary>
              </AlgoliaErrorBoundary>
            </AlgoliaCore>
          </S.Wrapper>

          {children}
        </ProductListContextProvider>
      </MasterLayout>
    </>
  );
};

export const ProductListView = memo(ProductListViewComponent, isEqual);
